import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { isAuthenticated, user } from '@stores/authStore';
import { api } from '@services/api';

interface ApiKey {
  id: number;
  name: string;
  key_preview: string;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState<number | null>(365);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated.value) {
      route('/login');
      return;
    }

    checkAccess();
  }, []);

  const checkAccess = async () => {
    // Admin always has access
    if (user.value?.is_admin) {
      loadApiKeys();
      return;
    }

    // Check if user is PM or developer in at least one project
    const response = await api.get<any>('/api/projects');
    if (response.success && response.data) {
      const projects = response.data.items || [];
      const hasPmOrDeveloperRole = projects.some(
        (p: any) => p.user_role === 'pm' || p.user_role === 'developer'
      );

      if (hasPmOrDeveloperRole) {
        loadApiKeys();
      } else {
        // Not PM or developer in any project, redirect to dashboard
        route('/dashboard');
      }
    } else {
      route('/dashboard');
    }
  };

  const loadApiKeys = async () => {
    setLoading(true);
    const response = await api.get<any>('/api/api-keys');
    if (response.success && response.data) {
      setApiKeys(response.data.items || []);
    }
    setLoading(false);
  };

  const handleCreateKey = async (e: Event) => {
    e.preventDefault();

    if (!newKeyName.trim()) {
      alert('API Key 이름을 입력해주세요');
      return;
    }

    const payload: any = {
      name: newKeyName.trim(),
    };

    if (expiresInDays !== null) {
      payload.expires_days = expiresInDays;
    }

    const response = await api.post<any>('/api/api-keys', payload);
    if (response.success && response.data) {
      setCreatedKey(response.data.key);
      setNewKeyName('');
      setExpiresInDays(365);
      loadApiKeys();
    } else {
      alert(response.error || 'API Key 생성 실패');
    }
  };

  const handleDeleteKey = async (keyId: number, keyName: string) => {
    if (!confirm(`"${keyName}" API Key를 삭제하시겠습니까?`)) {
      return;
    }

    const response = await api.delete(`/api/api-keys/${keyId}`);
    if (response.success) {
      loadApiKeys();
    } else {
      alert(response.error || 'API Key 삭제 실패');
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreatedKey(null);
    setNewKeyName('');
    setExpiresInDays(365);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('클립보드에 복사되었습니다');
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div class="min-h-screen bg-gray-100">
      <nav class="border-b bg-white shadow-sm">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="flex h-16 items-center justify-between">
            <div class="flex items-center">
              <button
                onClick={() => route('/dashboard')}
                class="text-xl font-bold text-blue-600 hover:text-blue-700"
              >
                Issue Tracker
              </button>
            </div>
            <div class="flex items-center gap-4">
              <button
                onClick={() => route('/dashboard')}
                class="text-gray-700 hover:text-gray-900"
              >
                대시보드
              </button>
              <span class="text-sm text-gray-600">{user.value?.name}</span>
            </div>
          </div>
        </div>
      </nav>

      <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div class="mb-8">
          <h1 class="text-3xl font-bold text-gray-900">API Keys</h1>
          <p class="mt-2 text-gray-600">
            외부 시스템이나 자동화 도구에서 Issue Tracker API에 접근하기 위한 API Key를 관리합니다.
          </p>
        </div>

        {/* API Keys List */}
        <div class="mb-8 rounded-lg bg-white p-6 shadow">
          <div class="mb-4 flex items-center justify-between">
            <h2 class="text-xl font-semibold">내 API Keys</h2>
            <button
              onClick={() => setShowCreateModal(true)}
              class="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              + 새 API Key 생성
            </button>
          </div>

          {loading ? (
            <p class="text-center text-gray-500">로딩 중...</p>
          ) : apiKeys.length === 0 ? (
            <p class="text-center text-gray-500">생성된 API Key가 없습니다.</p>
          ) : (
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="border-b bg-gray-50">
                    <th class="px-4 py-3 text-left text-sm font-semibold">이름</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold">Key Preview</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold">마지막 사용</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold">만료일</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold">생성일</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold">작업</th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((key) => (
                    <tr key={key.id} class="border-b hover:bg-gray-50">
                      <td class="px-4 py-3 text-sm">{key.name}</td>
                      <td class="px-4 py-3 text-sm font-mono">{key.key_preview}</td>
                      <td class="px-4 py-3 text-sm">{formatDate(key.last_used_at)}</td>
                      <td class="px-4 py-3 text-sm">
                        {key.expires_at ? (
                          <span class={new Date(key.expires_at) < new Date() ? 'text-red-600' : ''}>
                            {formatDate(key.expires_at)}
                          </span>
                        ) : (
                          <span class="text-gray-500">만료 없음</span>
                        )}
                      </td>
                      <td class="px-4 py-3 text-sm">{formatDate(key.created_at)}</td>
                      <td class="px-4 py-3 text-sm">
                        <button
                          onClick={() => handleDeleteKey(key.id, key.name)}
                          class="text-red-600 hover:text-red-800"
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* API Documentation */}
        <div class="rounded-lg bg-white p-6 shadow">
          <h2 class="mb-4 text-xl font-semibold">API 사용 메뉴얼</h2>

          <div class="space-y-6">
            {/* Authentication */}
            <div>
              <h3 class="mb-2 text-lg font-semibold text-gray-800">1. 인증 방법</h3>
              <p class="mb-2 text-sm text-gray-600">
                모든 API 요청에 Authorization 헤더를 포함해야 합니다:
              </p>
              <pre class="overflow-x-auto rounded bg-gray-900 p-4 text-sm text-green-400">
{`Authorization: Bearer isk_your_api_key_here`}
              </pre>
            </div>

            {/* Base URL */}
            <div>
              <h3 class="mb-2 text-lg font-semibold text-gray-800">2. Base URL</h3>
              <pre class="overflow-x-auto rounded bg-gray-900 p-4 text-sm text-green-400">
{`https://papi.dbitlabs.it`}
              </pre>
            </div>

            {/* Endpoints */}
            <div>
              <h3 class="mb-2 text-lg font-semibold text-gray-800">3. 주요 엔드포인트</h3>

              <div class="space-y-4">
                {/* Projects */}
                <div class="rounded border p-4">
                  <h4 class="mb-2 font-semibold text-gray-700">프로젝트</h4>
                  <ul class="space-y-1 text-sm">
                    <li><code class="text-blue-600">GET /api/projects</code> - 프로젝트 목록</li>
                    <li><code class="text-blue-600">GET /api/projects/:id</code> - 프로젝트 상세</li>
                  </ul>
                </div>

                {/* Issues */}
                <div class="rounded border p-4">
                  <h4 class="mb-2 font-semibold text-gray-700">이슈</h4>
                  <ul class="space-y-1 text-sm">
                    <li><code class="text-blue-600">GET /api/issues</code> - 이슈 목록 (project_id, status, priority 쿼리 가능, QA는 히든 이슈 제외)</li>
                    <li><code class="text-blue-600">GET /api/issues/:id</code> - 이슈 상세 (linked_issues 포함, QA는 히든 이슈 접근 불가)</li>
                    <li><code class="text-blue-600">GET /api/issues/by-number/:issue_number</code> - 이슈번호로 조회 (예: COINS-001)</li>
                    <li><code class="text-blue-600">POST /api/issues</code> - 이슈 생성 (is_hidden, linked_issue_numbers 지원)</li>
                    <li><code class="text-blue-600">PATCH /api/issues/:id</code> - 이슈 수정 (is_hidden, linked_issue_numbers 지원)</li>
                    <li><code class="text-blue-600">DELETE /api/issues/:id</code> - 이슈 삭제</li>
                  </ul>
                  <div class="mt-2 rounded bg-gray-50 p-2 text-xs text-gray-600">
                    <strong>linked_issue_numbers:</strong> 연결할 이슈 번호 배열 (예: ["COINS-001", "COINS-002"])
                  </div>
                </div>

                {/* Comments */}
                <div class="rounded border p-4">
                  <h4 class="mb-2 font-semibold text-gray-700">댓글</h4>
                  <ul class="space-y-1 text-sm">
                    <li><code class="text-blue-600">GET /api/comments?issue_id=:id</code> - 이슈의 댓글 목록</li>
                    <li><code class="text-blue-600">POST /api/comments</code> - 댓글 작성</li>
                    <li><code class="text-blue-600">PATCH /api/comments/:id</code> - 댓글 수정</li>
                    <li><code class="text-blue-600">DELETE /api/comments/:id</code> - 댓글 삭제</li>
                  </ul>
                </div>

                {/* Commits */}
                <div class="rounded border p-4">
                  <h4 class="mb-2 font-semibold text-gray-700">커밋</h4>
                  <ul class="space-y-1 text-sm">
                    <li><code class="text-blue-600">GET /api/commits?issue_id=:id</code> - 이슈의 커밋 목록</li>
                    <li><code class="text-blue-600">GET /api/commits?project_id=:id</code> - 프로젝트의 커밋 목록</li>
                    <li><code class="text-blue-600">POST /api/commits</code> - 커밋 직접 등록 (project_id, commit_hash, message, author, committed_at)</li>
                    <li><code class="text-blue-600">POST /api/commits/:issue_id/add</code> - 이슈에 커밋 추가 (commit_url_or_hash: Gitea URL 또는 커밋 해시)</li>
                  </ul>
                </div>

                {/* Wiki */}
                <div class="rounded border p-4">
                  <h4 class="mb-2 font-semibold text-gray-700">위키 (문서 관리) - QA 접근 불가</h4>
                  <ul class="space-y-1 text-sm">
                    <li><code class="text-blue-600">GET /api/projects/:id/wiki</code> - 위키 페이지 목록</li>
                    <li><code class="text-blue-600">GET /api/projects/:project_id/wiki/:page_id</code> - 위키 페이지 상세 (연결된 이슈 포함)</li>
                    <li><code class="text-blue-600">POST /api/projects/:id/wiki</code> - 위키 페이지 생성 (title, content, linked_issue_ids)</li>
                    <li><code class="text-blue-600">PUT /api/projects/:project_id/wiki/:page_id</code> - 위키 페이지 수정</li>
                    <li><code class="text-blue-600">DELETE /api/projects/:project_id/wiki/:page_id</code> - 위키 페이지 삭제</li>
                    <li><code class="text-blue-600">GET /api/issues/:id/wiki</code> - 이슈에 연결된 위키 목록</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Examples */}
            <div>
              <h3 class="mb-2 text-lg font-semibold text-gray-800">4. 사용 예제</h3>

              <div class="space-y-4">
                {/* curl example */}
                <div>
                  <h4 class="mb-2 text-sm font-semibold text-gray-700">curl 예제:</h4>
                  <pre class="overflow-x-auto rounded bg-gray-900 p-4 text-sm text-green-400">
{`# 이슈 목록 조회
curl -X GET "https://papi.dbitlabs.it/api/issues?project_id=1" \\
  -H "Authorization: Bearer isk_your_api_key_here"

# 이슈 생성
curl -X POST "https://papi.dbitlabs.it/api/issues" \\
  -H "Authorization: Bearer isk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "project_id": 1,
    "title": "버그 수정 필요",
    "description": "로그인 버튼이 작동하지 않습니다",
    "priority": "high",
    "issue_type": "bug",
    "is_hidden": false
  }'

# 이슈 생성 (연결된 이슈 포함)
curl -X POST "https://papi.dbitlabs.it/api/issues" \\
  -H "Authorization: Bearer isk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "project_id": 1,
    "title": "관련 기능 개선",
    "description": "기존 버그와 연관된 개선 작업",
    "priority": "medium",
    "issue_type": "improvement",
    "linked_issue_numbers": ["COINS-001", "COINS-002"]
  }'

# 이슈 수정 (연결된 이슈 추가)
curl -X PATCH "https://papi.dbitlabs.it/api/issues/1" \\
  -H "Authorization: Bearer isk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "linked_issue_numbers": ["COINS-003"]
  }'

# 위키 페이지 생성
curl -X POST "https://papi.dbitlabs.it/api/projects/1/wiki" \\
  -H "Authorization: Bearer isk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "API 개발 가이드",
    "content": "# API 개발 가이드\\n\\n## 개요\\n이 문서는...",
    "linked_issue_ids": [1, 2]
  }'

# 이슈에 커밋 추가 (Gitea URL)
curl -X POST "https://papi.dbitlabs.it/api/commits/1/add" \\
  -H "Authorization: Bearer isk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "commit_url_or_hash": "http://gitea.example.com/owner/repo/commit/a1b2c3d4e5f6"
  }'

# 이슈에 커밋 추가 (커밋 해시만)
curl -X POST "https://papi.dbitlabs.it/api/commits/1/add" \\
  -H "Authorization: Bearer isk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "commit_url_or_hash": "a1b2c3d4e5f6"
  }'

# 커밋 직접 등록
curl -X POST "https://papi.dbitlabs.it/api/commits" \\
  -H "Authorization: Bearer isk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "project_id": 1,
    "commit_hash": "a1b2c3d4e5f6",
    "message": "Fix authentication bug",
    "author": "John Doe",
    "committed_at": "2025-11-11T10:30:00Z"
  }'`}
                  </pre>
                </div>

                {/* Python example */}
                <div>
                  <h4 class="mb-2 text-sm font-semibold text-gray-700">Python 예제:</h4>
                  <pre class="overflow-x-auto rounded bg-gray-900 p-4 text-sm text-green-400">
{`import requests

API_KEY = "isk_your_api_key_here"
BASE_URL = "https://papi.dbitlabs.it"

headers = {
    "Authorization": f"Bearer {API_KEY}",
    "Content-Type": "application/json"
}

# 이슈 목록 조회
response = requests.get(
    f"{BASE_URL}/api/issues",
    params={"project_id": 1, "status": "new"},
    headers=headers
)
issues = response.json()

# 댓글 작성
response = requests.post(
    f"{BASE_URL}/api/comments",
    json={"issue_id": 1, "content": "작업 중입니다"},
    headers=headers
)
comment = response.json()

# 이슈 생성 (연결된 이슈 포함)
response = requests.post(
    f"{BASE_URL}/api/issues",
    json={
        "project_id": 1,
        "title": "관련 기능 개선",
        "description": "기존 버그와 연관된 개선 작업",
        "priority": "medium",
        "issue_type": "improvement",
        "linked_issue_numbers": ["COINS-001", "COINS-002"]
    },
    headers=headers
)
new_issue = response.json()

# 이슈 수정 (연결된 이슈 추가)
response = requests.patch(
    f"{BASE_URL}/api/issues/1",
    json={"linked_issue_numbers": ["COINS-003"]},
    headers=headers
)
updated_issue = response.json()

# 위키 페이지 생성
response = requests.post(
    f"{BASE_URL}/api/projects/1/wiki",
    json={
        "title": "프로젝트 아키텍처",
        "content": "# 아키텍처 문서\\n\\n## 시스템 구조...",
        "linked_issue_ids": [1, 2, 3]
    },
    headers=headers
)
wiki_page = response.json()

# 이슈에 커밋 추가 (Gitea URL)
response = requests.post(
    f"{BASE_URL}/api/commits/1/add",
    json={"commit_url_or_hash": "http://gitea.example.com/owner/repo/commit/a1b2c3d4e5f6"},
    headers=headers
)

# 이슈에 커밋 추가 (커밋 해시만)
response = requests.post(
    f"{BASE_URL}/api/commits/1/add",
    json={"commit_url_or_hash": "a1b2c3d4e5f6"},
    headers=headers
)

# 커밋 직접 등록
response = requests.post(
    f"{BASE_URL}/api/commits",
    json={
        "project_id": 1,
        "commit_hash": "a1b2c3d4e5f6",
        "message": "Fix authentication bug",
        "author": "John Doe",
        "committed_at": "2025-11-11T10:30:00Z"
    },
    headers=headers
)
commit = response.json()`}
                  </pre>
                </div>

                {/* JavaScript example */}
                <div>
                  <h4 class="mb-2 text-sm font-semibold text-gray-700">JavaScript 예제:</h4>
                  <pre class="overflow-x-auto rounded bg-gray-900 p-4 text-sm text-green-400">
{`const API_KEY = "isk_your_api_key_here";
const BASE_URL = "https://papi.dbitlabs.it";

const headers = {
  "Authorization": \`Bearer \${API_KEY}\`,
  "Content-Type": "application/json"
};

// 이슈 목록 조회
const issues = await fetch(
  \`\${BASE_URL}/api/issues?project_id=1\`,
  { headers }
).then(r => r.json());

// 이슈 수정
const updated = await fetch(
  \`\${BASE_URL}/api/issues/1\`,
  {
    method: "PATCH",
    headers,
    body: JSON.stringify({
      status: "in_progress",
      is_hidden: false
    })
  }
).then(r => r.json());

// 이슈 생성 (연결된 이슈 포함)
const newIssue = await fetch(
  \`\${BASE_URL}/api/issues\`,
  {
    method: "POST",
    headers,
    body: JSON.stringify({
      project_id: 1,
      title: "관련 기능 개선",
      description: "기존 버그와 연관된 개선 작업",
      priority: "medium",
      issue_type: "improvement",
      linked_issue_numbers: ["COINS-001", "COINS-002"]
    })
  }
).then(r => r.json());

// 이슈 수정 (연결된 이슈 추가)
const linkedIssue = await fetch(
  \`\${BASE_URL}/api/issues/1\`,
  {
    method: "PATCH",
    headers,
    body: JSON.stringify({
      linked_issue_numbers: ["COINS-003"]
    })
  }
).then(r => r.json());

// 위키 페이지 생성
const wiki = await fetch(
  \`\${BASE_URL}/api/projects/1/wiki\`,
  {
    method: "POST",
    headers,
    body: JSON.stringify({
      title: "프로젝트 개요",
      content: "# 프로젝트 개요\\n\\n이 프로젝트는...",
      linked_issue_ids: [1, 2, 3]
    })
  }
).then(r => r.json());

// 위키 페이지 목록 조회
const wikiPages = await fetch(
  \`\${BASE_URL}/api/projects/1/wiki\`,
  { headers }
).then(r => r.json());

// 이슈에 커밋 추가 (Gitea URL)
await fetch(
  \`\${BASE_URL}/api/commits/1/add\`,
  {
    method: "POST",
    headers,
    body: JSON.stringify({
      commit_url_or_hash: "http://gitea.example.com/owner/repo/commit/a1b2c3d4e5f6"
    })
  }
).then(r => r.json());

// 이슈에 커밋 추가 (커밋 해시만)
await fetch(
  \`\${BASE_URL}/api/commits/1/add\`,
  {
    method: "POST",
    headers,
    body: JSON.stringify({
      commit_url_or_hash: "a1b2c3d4e5f6"
    })
  }
).then(r => r.json());

// 커밋 직접 등록
const commit = await fetch(
  \`\${BASE_URL}/api/commits\`,
  {
    method: "POST",
    headers,
    body: JSON.stringify({
      project_id: 1,
      commit_hash: "a1b2c3d4e5f6",
      message: "Fix authentication bug",
      author: "John Doe",
      committed_at: "2025-11-11T10:30:00Z"
    })
  }
).then(r => r.json());`}
                  </pre>
                </div>
              </div>
            </div>

            {/* Response Format */}
            <div>
              <h3 class="mb-2 text-lg font-semibold text-gray-800">5. 응답 형식</h3>
              <p class="mb-2 text-sm text-gray-600">모든 API 응답은 다음 형식을 따릅니다:</p>
              <pre class="overflow-x-auto rounded bg-gray-900 p-4 text-sm text-green-400">
{`// 성공
{
  "success": true,
  "data": { ... }
}

// 실패
{
  "success": false,
  "error": "에러 메시지"
}`}
              </pre>
            </div>

            {/* Security Notes */}
            <div class="rounded bg-yellow-50 p-4">
              <h3 class="mb-2 text-lg font-semibold text-yellow-800">보안 주의사항</h3>
              <ul class="list-inside list-disc space-y-1 text-sm text-yellow-700">
                <li>API Key는 생성 시 한 번만 표시됩니다. 안전한 곳에 보관하세요.</li>
                <li>API Key를 코드 저장소에 직접 포함하지 마세요.</li>
                <li>환경 변수나 비밀 관리 시스템을 사용하세요.</li>
                <li>사용하지 않는 API Key는 즉시 삭제하세요.</li>
                <li>API Key가 노출되었다면 즉시 삭제하고 새로 발급받으세요.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div class="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            {createdKey ? (
              // Show created key
              <div>
                <h3 class="mb-4 text-xl font-semibold">API Key 생성 완료</h3>
                <div class="mb-4 rounded bg-yellow-50 p-4">
                  <p class="mb-2 text-sm text-yellow-800 font-semibold">
                    ⚠️ 이 API Key는 지금 한 번만 표시됩니다!
                  </p>
                  <p class="text-sm text-yellow-700">
                    안전한 곳에 복사해서 보관하세요. 다시 확인할 수 없습니다.
                  </p>
                </div>
                <div class="mb-4">
                  <label class="mb-2 block text-sm font-semibold">API Key:</label>
                  <div class="flex gap-2">
                    <input
                      type="text"
                      value={createdKey}
                      readOnly
                      class="flex-1 rounded border px-3 py-2 font-mono text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(createdKey)}
                      class="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    >
                      복사
                    </button>
                  </div>
                </div>
                <button
                  onClick={closeCreateModal}
                  class="w-full rounded bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
                >
                  닫기
                </button>
              </div>
            ) : (
              // Create form
              <form onSubmit={handleCreateKey}>
                <h3 class="mb-4 text-xl font-semibold">새 API Key 생성</h3>

                <div class="mb-4">
                  <label class="mb-2 block text-sm font-semibold">
                    이름 <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newKeyName}
                    onInput={(e) => setNewKeyName((e.target as HTMLInputElement).value)}
                    placeholder="예: Claude MCP Server"
                    class="w-full rounded border px-3 py-2"
                    required
                  />
                  <p class="mt-1 text-xs text-gray-500">
                    용도를 알 수 있는 이름을 입력하세요
                  </p>
                </div>

                <div class="mb-4">
                  <label class="mb-2 block text-sm font-semibold">만료일</label>
                  <select
                    value={expiresInDays ?? ''}
                    onChange={(e) => {
                      const val = (e.target as HTMLSelectElement).value;
                      setExpiresInDays(val === '' ? null : parseInt(val));
                    }}
                    class="w-full rounded border px-3 py-2"
                  >
                    <option value="30">30일 후</option>
                    <option value="90">90일 후</option>
                    <option value="365">1년 후</option>
                    <option value="">만료 없음</option>
                  </select>
                </div>

                <div class="flex gap-2">
                  <button
                    type="button"
                    onClick={closeCreateModal}
                    class="flex-1 rounded border px-4 py-2 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    class="flex-1 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                  >
                    생성
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
